import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, CheckCircle2, Circle, XCircle } from 'lucide-react'
import { api } from '@/lib/api/client'
import { formatDate } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CardSkeleton } from '@/components/common/skeleton'
import { ErrorState } from '@/components/common/error-state'

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>()

  const query = useQuery({
    queryKey: ['event', id],
    queryFn: () => api.getEvent(id!),
    enabled: !!id,
  })

  if (query.isLoading) {
    return (
      <div className="space-y-6">
        <CardSkeleton />
        <CardSkeleton />
      </div>
    )
  }

  if (query.isError || !query.data) {
    return (
      <ErrorState
        message={query.error?.message ?? 'Event not found'}
        onRetry={() => query.refetch()}
      />
    )
  }

  const event = query.data

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/events">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4" />
            Back to events
          </Button>
        </Link>
      </div>

      <div>
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-semibold tracking-tight">{event.event_name}</h2>
          <Badge variant="outline">{event.brand_id}</Badge>
        </div>
        <p className="mt-1 font-mono text-sm text-muted-foreground">{event.event_id}</p>
      </div>

      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="py-4 text-sm text-amber-900">
          <strong>Level A observability:</strong> Dispatch status shows success/failure and
          timestamp only. Raw channel HTTP request/response bodies are not stored or displayed.
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Event timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="relative space-y-0 border-l border-border pl-6">
            <TimelineStep
              title="BO received"
              timestamp={event.received_at}
              status="complete"
              detail={`Event ${event.event_name} ingested via capi-mirror`}
            />

            <TimelineStep
              title="Mirror archived"
              timestamp={event.received_at}
              status="complete"
              detail="Stored in capi-events-mirror (DDB)"
            />

            {event.attribution ? (
              <TimelineStep
                title="Match decision"
                timestamp={event.attribution.matched_at ?? undefined}
                status="complete"
                detail={
                  <>
                    Tier: <strong>{event.attribution.tier ?? 'unknown'}</strong>
                    {' · '}
                    Source: <strong>{event.attribution.source ?? 'unknown'}</strong>
                    {event.attribution.ref && (
                      <>
                        {' · '}
                        Ref: <code className="text-xs">{event.attribution.ref}</code>
                      </>
                    )}
                  </>
                }
              />
            ) : (
              <TimelineStep
                title="Match decision"
                status="pending"
                detail="No attribution match recorded"
              />
            )}

            {event.dispatch.length === 0 ? (
              <TimelineStep
                title="Channel dispatch"
                status="pending"
                detail="No dispatches recorded"
              />
            ) : (
              event.dispatch.map((d, i) => (
                <TimelineStep
                  key={`${d.channel}-${i}`}
                  title={`Dispatch: ${d.channel}`}
                  timestamp={d.sent_at ?? undefined}
                  status={
                    d.status === 'success' || d.status === 'sent'
                      ? 'complete'
                      : d.status === 'failed' || d.status === 'error'
                        ? 'error'
                        : 'pending'
                  }
                  detail={
                    <>
                      Event type: {d.event_type}
                      {d.status && (
                        <>
                          {' · '}
                          <Badge
                            variant={
                              d.status === 'success' || d.status === 'sent'
                                ? 'success'
                                : d.status === 'failed' || d.status === 'error'
                                  ? 'destructive'
                                  : 'secondary'
                            }
                          >
                            {d.status}
                          </Badge>
                        </>
                      )}
                    </>
                  }
                />
              ))
            )}
          </ol>

          {event.timeline.length > 0 && (
            <div className="mt-8 border-t pt-6">
              <h4 className="mb-3 text-sm font-medium text-muted-foreground">
                Extended timeline
              </h4>
              <pre className="overflow-auto rounded-md bg-muted p-4 text-xs">
                {JSON.stringify(event.timeline, null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Event metadata</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">FN (user hash)</dt>
              <dd className="font-mono text-xs">{event.fn}</dd>
            </div>
            {event.username && (
              <div>
                <dt className="text-muted-foreground">Username</dt>
                <dd>{event.username}</dd>
              </div>
            )}
            <div>
              <dt className="text-muted-foreground">Received at</dt>
              <dd>{formatDate(event.received_at)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  )
}

function TimelineStep({
  title,
  timestamp,
  status,
  detail,
}: {
  title: string
  timestamp?: string
  status: 'complete' | 'pending' | 'error'
  detail: React.ReactNode
}) {
  const Icon =
    status === 'complete' ? CheckCircle2 : status === 'error' ? XCircle : Circle

  const iconColor =
    status === 'complete'
      ? 'text-emerald-600'
      : status === 'error'
        ? 'text-red-600'
        : 'text-muted-foreground'

  return (
    <li className="relative pb-8 last:pb-0">
      <span
        className={`absolute -left-[1.65rem] flex h-6 w-6 items-center justify-center rounded-full bg-background ${iconColor}`}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <p className="font-medium">{title}</p>
        {timestamp && (
          <p className="text-xs text-muted-foreground">{formatDate(timestamp)}</p>
        )}
        <p className="mt-1 text-sm text-muted-foreground">{detail}</p>
      </div>
    </li>
  )
}
